import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import React from 'react'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { CollapsibleList } from '../components/CollapsibleList'
import { Contained } from '../components/Contained'
import { Content } from '../components/Content'
import { Intro } from '../components/Intro'
import { MissingContent } from '../components/MissingContent'
import { RadioButtons } from '../components/RadioButtons'
import { Seo } from '../components/Seo'
import { Spacer } from '../components/Spacer'
import { WidthBound } from '../components/WidthBound'
import { CareerPositionContextProvider } from '../src/contexts/CareerPositionContext'
import { usePageLink } from '../src/hooks/usePageLink'
import { CareerCategoryFragment, CareerPositionFragment, PageFragment, SpecialPageType } from '../src/model/fragments'
import { getLocale, getPossibleLocales, Locale } from '../src/model/locale'
import { getLinkWithQuery } from '../src/model/urls'
import { axiosGet } from '../src/utils/axiosWrapper'

export default function Career({
	page,
	possibleLocales,
	careerPositions,
	careerCategories,
	categoryId,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
	const anchorRef = React.useRef<HTMLDivElement>(null)

	const allCategoriesLink = usePageLink(SpecialPageType.career)!
	const careerCategoryLink = (category: null | string) => ({
		...(category === null
			? allCategoriesLink
			: getLinkWithQuery(allCategoriesLink, { category })),
		scroll: false,
	})

	const [fastCategoryId, setFastCategoryId] = React.useState(categoryId)
	const index = React.useMemo(
		() =>
			1 +
			careerCategories.findIndex((category) => category.id === fastCategoryId),
		[careerCategories, fastCategoryId],
	)
	const [initialIndex] = React.useState(index)

	// Keep ids in sync
	React.useEffect(() => {
		if (fastCategoryId === categoryId) {
			return
		}
		anchorRef.current?.scrollIntoView({
			behavior: 'smooth',
		})
		setFastCategoryId(categoryId)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [categoryId])

	if (!page) {
		return <MissingContent />
	}

	const data = getLocale(page.locales, possibleLocales)

	if (!data) {
		return <MissingContent />
	}

	const positions = careerPositions
	.filter(
		(item) =>
			fastCategoryId === null || item.category?.id === fastCategoryId,
	);

	return (
		<>
			<Seo seo={data.seo} />
			<Breadcrumbs
				items={[
					{
						label: data.title,
					},
				]}
			/>
			<WidthBound>
				<Intro title={data.title}>
					<Content content={data.content} innerSize="narrow" />
				</Intro>
			</WidthBound>
			<Contained>
				<div ref={anchorRef} />
				<Spacer initial={20} />
				<WidthBound>
					<RadioButtons
						items={[
							{ text: 'Vše', linkProps: careerCategoryLink(null) },
							...careerCategories.map((category) => ({
								text: category.name,
								linkProps: careerCategoryLink(category.id),
							})),
						]}
						onChange={(selectedIndex) => {
							setFastCategoryId(
								selectedIndex === 0
									? null
									: Object.values(careerCategories)[selectedIndex - 1].id,
							)
						}}
						index={index}
						initialIndex={initialIndex}
					/>
				</WidthBound>
				{ positions.length ? <CollapsibleList
					items={positions.map((item) => ({
							label: item.title,
							content: (
								<CareerPositionContextProvider
									message={`${item.category?.name || ''}: ${item.title}\n`}
								>
									<Content content={item.content} viewQuestionOrCareerList />
								</CareerPositionContextProvider>
							),
						}))}
					large
				/> : '' }
				{ !positions.length ? <WidthBound>
					<p>Ve vybraném kraji nemáme aktuálně otevřené pozice.</p>
				</WidthBound> : ''}
				<Spacer initial={100} />
			</Contained>
		</>
	)
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
	const locale = ctx.query.locale as Locale
	const possibleLocales = getPossibleLocales(locale)
	const queryParams = ctx.query

	const categoryId =
		typeof queryParams.category === 'string' && queryParams.category
			? queryParams.category
			: null

	const {
		getPage: page,
		listCareerPosition: careerPositions,
		listCareerCategory: careerCategories,
	} = await axiosGet('/career', {
		navigationId: ctx.query.id,
		locale: locale,
		categoryId
	}) as {
		getPage: PageFragment,
		listCareerPosition: CareerPositionFragment[],
		listCareerCategory: CareerCategoryFragment[],
	}
	
	return {
		props: {
			page,
			possibleLocales,
			careerPositions,
			careerCategories,
			categoryId,
		},
	}
}
